import React, { useEffect, useMemo, useState } from "react";
import { NuovoContoModal } from "@/components/prima_nota/VocePicker";
import api from "@/lib/api";

const DARK = "#0d47a1", MED = "#1565c0", SUB = "#90caf9";

export default function PrimaNotaBilancioSchemaPage() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filtro, setFiltro] = useState("all");
  const [msg, setMsg] = useState(null);
  const [pivotTot, setPivotTot] = useState({});
  const [newConto, setNewConto] = useState(null);

  const carica = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get("/pn/bilancio-voci"),
        api.get("/pn/report/bilancio", { params: { source: "fatture", dual: true } }),
      ]);
      const data = r.data.data || [];
      setRows(data);
      const d = {};
      data.forEach(v => {
        d[v.id_conto] = {
          descrizione: v.descrizione || "",
          c0: v.c0 ?? "", c1: v.c1 ?? "", c2: v.c2 ?? "", c3: v.c3 ?? "",
        };
      });
      setDrafts(d);
      const pt = {};
      (p.data?.data || []).forEach(x => { const f = x.fatture || x; pt[x.id_conto] = f.tot || 0; });
      setPivotTot(pt);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  const fmtEur = (n) => (n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  useEffect(() => { carica(); /* eslint-disable-next-line */ }, []);

  const origById = useMemo(() => {
    const m = {};
    rows.forEach(v => { m[v.id_conto] = v; });
    return m;
  }, [rows]);

  const ricaviC0 = useMemo(() => {
    const s = rows.filter(v => v.livello === 1);
    return s.find(v => (v.descrizione || "").toLowerCase().includes("ricav"))?.c0 ?? 2;
  }, [rows]);
  const costiC0 = useMemo(() => {
    const s = rows.filter(v => v.livello === 1);
    return s.find(v => v.c0 !== ricaviC0)?.c0 ?? 1;
  }, [rows, ricaviC0]);

  const changedFields = (id) => {
    const o = origById[id]; const d = drafts[id];
    if (!o || !d) return {};
    const out = {};
    if ((d.descrizione || "") !== (o.descrizione || "")) out.descrizione = d.descrizione;
    for (const k of ["c0", "c1", "c2", "c3"]) {
      const dv = d[k] === "" || d[k] === null ? null : Number(d[k]);
      const ov = o[k] ?? null;
      if (dv !== null && dv !== ov) out[k] = dv;
    }
    return out;
  };

  const dirtyIds = useMemo(
    () => rows.map(r => r.id_conto).filter(id => Object.keys(changedFields(id)).length > 0),
    // eslint-disable-next-line
    [rows, drafts, origById]
  );

  const setField = (id, k, val) => setDrafts(d => ({ ...d, [id]: { ...d[id], [k]: val } }));

  const salva = async () => {
    if (dirtyIds.length === 0) return;
    setSaving(true); setMsg(null);
    let ok = 0, fail = 0, lastErr = "";
    for (const id of dirtyIds) {
      const payload = changedFields(id);
      if (Object.keys(payload).length === 0) continue;
      try { await api.put(`/pn/bilancio-voci/${id}`, payload); ok++; }
      catch (e) { fail++; lastErr = e.response?.data?.detail || "errore"; }
    }
    setSaving(false);
    setMsg(fail === 0 ? `✓ Salvate ${ok} voci` : `Salvate ${ok}, ${fail} errori (${lastErr})`);
    await carica();
  };

  const elimina = async (r) => {
    const nome = (r.descrizione || "").trim();
    const t = rowTotal(r);
    const warn = t ? `\n\n⚠️ Questo conto ha movimenti collegati per € ${fmtEur(t)}.` : "";
    if (!window.confirm(`Eliminare il conto [${r.id_conto}] "${nome}"?${warn}\n\nL'operazione fallirà se ci sono righe contabili, mapping causali o sotto-conti collegati.`)) return;
    setMsg(null);
    try {
      await api.delete(`/pn/bilancio-voci/${r.id_conto}`);
      setMsg(`✓ Eliminato [${r.id_conto}] ${nome}`);
      await carica();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Errore durante l'eliminazione");
    }
  };

  const normalizza = async () => {
    if (!window.confirm("Normalizzare il piano dei conti?\n\n• Appiattisce a 3 livelli (mastro → conto → sottoconto)\n• Rinumera conti e sottoconti in sequenza senza buchi\n• I collegamenti con le fatture restano intatti (id_conto invariati)")) return;
    setSaving(true); setMsg(null);
    try {
      const r = await api.post("/pn/bilancio-voci/normalize");
      const d = r.data || {};
      setMsg(`✓ Normalizzato: ${d.conti_rinumerati || 0} conti, ${d.sottoconti_rinumerati || 0} sottoconti${d.sottogruppi_rimossi ? `, ${d.sottogruppi_rimossi} sotto-gruppi rimossi` : ""}`);
      await carica();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Errore durante la normalizzazione");
    } finally {
      setSaving(false);
    }
  };

  const rinumeraConto = async (r) => {
    const nome = (r.descrizione || "").trim();
    const input = window.prompt(`Nuovo numero per il CONTO ${r.c0}.${r.c1} "${nome}"?\n\nTutti i suoi sottoconti verranno spostati al nuovo conto.`, String(r.c1));
    if (input === null) return;
    const new_c1 = parseInt(input, 10);
    if (isNaN(new_c1) || new_c1 < 1) { setMsg("Numero non valido"); return; }
    if (new_c1 === r.c1) return;
    setSaving(true); setMsg(null);
    try {
      const res = await api.post(`/pn/bilancio-voci/${r.id_conto}/rinumera`, { new_c1 });
      setMsg(`✓ Conto spostato a ${r.c0}.${new_c1} — ${res.data?.spostati || 0} voci aggiornate (conto + sottoconti)`);
      await carica();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Errore durante la rinumerazione");
    } finally {
      setSaving(false);
    }
  };

  const rowsSorted = useMemo(() => {
    const key = (r) => [
      r.c0 === ricaviC0 ? 0 : 1,
      r.c0 ?? 999,
      r.livello === 1 ? 0 : 1,
      r.c1 ?? 0,
      r.c2 ?? 0,
      r.is_foglia ? 1 : 0,
      r.c3 ?? 0,
    ];
    return [...rows].sort((a, b) => {
      const ka = key(a), kb = key(b);
      for (let i = 0; i < ka.length; i++) { if (ka[i] !== kb[i]) return ka[i] - kb[i]; }
      return 0;
    });
  }, [rows, ricaviC0]);
  const rowsFiltered = rowsSorted.filter(r => filtro === "all" || Number(r.c0) === Number(filtro));

  const aggTot = useMemo(() => {
    const byC0 = {}, byC1 = {}, leaf = {};
    rows.forEach(v => {
      if (!v.is_foglia) return;
      const t = pivotTot[v.id_conto] || 0;
      leaf[v.id_conto] = t;
      byC0[v.c0] = (byC0[v.c0] || 0) + t;
      if (v.c1 != null) { const k = `${v.c0}.${v.c1}`; byC1[k] = (byC1[k] || 0) + t; }
    });
    return { byC0, byC1, leaf };
  }, [rows, pivotTot]);

  const rowTotal = (r) => {
    if (r.is_foglia) return aggTot.leaf[r.id_conto] || 0;
    if (r.livello === 1) return aggTot.byC0[r.c0] || 0;
    if (r.livello === 2) return aggTot.byC1[`${r.c0}.${r.c1}`] || 0;
    return 0;
  };

  const rowStyle = (r) => {
    if (r.livello === 1) return { background: DARK, color: "white", fontWeight: 800 };
    if (r.livello === 2) return { background: MED, color: "white", fontWeight: 700 };
    if (r.livello === 3) return { background: SUB, color: "#0d47a1", fontWeight: 700 };
    return { background: "white", color: "#263238" };
  };

  const codeInput = (r, field, editable) => {
    const d = drafts[r.id_conto] || {};
    const val = d[field];
    if (!editable) {
      return <span style={{ fontFamily: "monospace", fontWeight: 700, color: r.livello <= 2 ? "#ffffff" : "#90a4ae" }}>{val === "" || val == null ? "" : val}</span>;
    }
    return (
      <input type="number" value={val ?? ""} onChange={e => setField(r.id_conto, field, e.target.value)}
        data-testid={`bil-schema-${field}-${r.id_conto}`}
        style={{ width: 56, padding: "3px 6px", border: "1px solid #90caf9", fontFamily: "monospace", fontSize: 12, fontWeight: 700, background: "white", color: "#0d47a1", textAlign: "center" }} />
    );
  };

  return (
    <div data-testid="bil-schema-page">
      <div className="sw-page-title">
        <h1>📋 Bilancio Schema — Piano dei Conti</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {msg && <span data-testid="bil-schema-msg" style={{ fontSize: 12, fontWeight: 700, color: msg.startsWith("✓") ? "#1b5e20" : "#c62828" }}>{msg}</span>}
          <button onClick={() => setNewConto({ c0: null, c1: null, c2: null, c3: null, descrizione: "" })} data-testid="bil-schema-newconto"
            style={{ padding: "8px 18px", background: "#fbc02d", color: "#0d47a1", border: "2px solid #f57f17", fontSize: 13, fontWeight: 800, cursor: "pointer", boxShadow: "0 2px 6px rgba(245,127,23,0.3)" }}>
            + NUOVO CONTO
          </button>
          <button onClick={carica} disabled={loading} data-testid="bil-schema-reload"
            style={{ padding: "5px 12px", background: "#37474f", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            {loading ? "..." : "↻ Ricarica"}
          </button>
          <button onClick={normalizza} disabled={saving || loading} data-testid="bil-schema-normalize"
            style={{ padding: "5px 12px", background: "#6a1b9a", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
            title="Appiattisce a 3 livelli e rinumera conti e sottoconti in sequenza (1.1.1, 1.1.2 …)">
            ⚙ Normalizza
          </button>
          <button onClick={salva} disabled={saving || dirtyIds.length === 0} data-testid="bil-schema-save"
            style={{ padding: "8px 18px", background: dirtyIds.length === 0 ? "#ccc" : "#1b5e20", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: dirtyIds.length === 0 ? "not-allowed" : "pointer" }}>
            {saving ? "Salvataggio..." : `💾 Salva modifiche${dirtyIds.length ? ` (${dirtyIds.length})` : ""}`}
          </button>
        </div>
      </div>

      <div style={{ margin: "0 20px 8px", fontSize: 12, color: "#666" }}>
        Modifica descrizione e codici di livello (MASTRO / CONTO / SOTTOCONTO). Il <b>COD.</b> 🔒 non è modificabile perché collega i costi delle fatture associate.
      </div>

      <div style={{ margin: "0 20px", display: "flex", gap: 0, borderBottom: "2px solid #37474f" }}>
        {[{ k: "all", l: "Tutti", c: "#37474f" }, { k: ricaviC0, l: "Ricavi", c: "#2e7d32" }, { k: costiC0, l: "Costi", c: "#1565c0" }].map(t => {
          const active = String(filtro) === String(t.k);
          return (
            <button key={t.k} onClick={() => setFiltro(t.k)} data-testid={`bil-schema-tab-${t.k}`}
              style={{ padding: "6px 18px", background: active ? t.c : "#eee", color: active ? "white" : "#666", border: "none", borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{t.l}</button>
          );
        })}
      </div>

      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc", borderTop: 0, maxHeight: "72vh", overflow: "auto" }}>
        <table className="sw-table sw-table-compact" style={{ fontSize: 12 }}>
          <thead style={{ position: "sticky", top: 0, background: "#37474f", color: "white", zIndex: 1 }}>
            <tr>
              <th style={{ width: 80, textAlign: "center", background: "#37474f", color: "white", padding: "8px 6px" }}>COD. 🔒</th>
              <th style={{ width: 110, textAlign: "center", background: "#37474f", color: "white", padding: "8px 6px" }}>MASTRO</th>
              <th style={{ width: 110, textAlign: "center", background: "#37474f", color: "white", padding: "8px 6px" }}>CONTO</th>
              <th style={{ width: 130, textAlign: "center", background: "#37474f", color: "white", padding: "8px 6px" }}>SOTTOCONTO</th>
              <th style={{ textAlign: "left", background: "#37474f", color: "white", padding: "8px 10px" }}>DESCRIZIONE</th>
              <th style={{ width: 150, textAlign: "right", background: "#1b5e20", color: "white", padding: "8px 10px" }}>TOTALE MOVIMENTI €</th>
              <th style={{ width: 44, background: "#37474f", color: "white", padding: "8px 4px" }}></th>
            </tr>
          </thead>
          <tbody>
            {rowsFiltered.length === 0 && <tr><td colSpan={7} style={{ padding: 30, textAlign: "center", color: "#888" }}>{loading ? "Caricamento..." : "Nessuna voce"}</td></tr>}
            {rowsFiltered.map(r => {
              const st = rowStyle(r);
              const d = drafts[r.id_conto] || {};
              const foglia = r.is_foglia;
              const lv = r.livello;
              const indent = Math.max(0, (lv - 1)) * 18;
              const dirty = Object.keys(changedFields(r.id_conto)).length > 0;
              const hasMov = rowTotal(r) > 0;
              return (
                <tr key={r.id_conto} data-testid={`bil-schema-row-${r.id_conto}`} style={st}>
                  <td style={{ textAlign: "center", fontFamily: "monospace", fontWeight: 800, background: st.background, color: st.color, borderLeft: dirty ? "4px solid #fbc02d" : "4px solid transparent" }}>{foglia ? (r.id_conto ?? "—") : ""}</td>
                  <td style={{ textAlign: "center", background: st.background }}>{codeInput(r, "c0", false)}</td>
                  <td style={{ textAlign: "center", background: st.background }}>{lv === 1 ? null : codeInput(r, "c1", lv !== 2)}</td>
                  <td style={{ textAlign: "center", background: st.background }}>{foglia ? codeInput(r, "c3", true) : (lv === 3 ? codeInput(r, "c2", true) : null)}</td>
                  <td style={{ background: st.background, paddingLeft: 10 + indent }}>
                    <input type="text" value={d.descrizione ?? ""} onChange={e => setField(r.id_conto, "descrizione", e.target.value)}
                      data-testid={`bil-schema-descr-${r.id_conto}`}
                      style={{ width: "95%", padding: "4px 8px", border: lv <= 2 ? "1px solid rgba(255,255,255,0.45)" : "1px solid #ccc", fontSize: lv <= 2 ? 13 : 12, fontWeight: lv <= 2 ? 800 : 500, background: lv <= 2 ? "transparent" : "white", color: lv <= 2 ? "white" : "#263238" }} />
                  </td>
                  <td data-testid={`bil-schema-tot-${r.id_conto}`} title={foglia ? "Totale movimenti costi (fatture) collegati a questo conto" : "Totale complessivo dei sottoconti"}
                    style={{ textAlign: "right", fontFamily: "monospace", background: st.background, color: st.color === "white" ? "white" : (rowTotal(r) ? "#1b5e20" : "#bbb"), fontWeight: r.livello <= 2 ? 800 : 700, paddingRight: 12 }}>
                    {rowTotal(r) ? fmtEur(rowTotal(r)) : "—"}
                  </td>
                  <td style={{ textAlign: "center", background: st.background, whiteSpace: "nowrap" }}>
                    {lv === 2 && (
                      <button onClick={() => rinumeraConto(r)} data-testid={`bil-schema-renum-${r.id_conto}`} title="Cambia numero del conto e sposta tutti i suoi sottoconti"
                        style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 14, lineHeight: 1, color: st.color === "white" ? "white" : "#0d47a1", marginRight: 6 }}>↕</button>
                    )}
                    <button onClick={() => elimina(r)} disabled={hasMov} data-testid={`bil-schema-del-${r.id_conto}`}
                      title={hasMov ? "Non eliminabile: ha costi/ricavi collegati" : "Elimina conto"}
                      style={{ background: "transparent", border: "none", cursor: hasMov ? "not-allowed" : "pointer", fontSize: 14, lineHeight: 1, opacity: hasMov ? 0.3 : 1, color: st.color === "white" ? "#ffcdd2" : "#c62828" }}>🗑</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {newConto && (
        <NuovoContoModal draft={newConto} setDraft={setNewConto}
          onClose={() => setNewConto(null)}
          onCreated={() => { setNewConto(null); carica(); }} />
      )}
    </div>
  );
}
